/**
 * WindowDragCloseTarget
 *
 * A drop target that appears when a window is being dragged.
 * Dragging a window into the drop zone (5× the icon radius) closes it.
 *
 * Uses direct DOM manipulation for hover state to avoid React batching
 * delays during rapid pointermove events.
 */

import { useEffect, useRef } from "react";
import { useThemeOptional } from "../contexts/ThemeContext";

const TARGET_SIZE = 52;
const DROP_ZONE_RADIUS = (TARGET_SIZE / 2) * 5; // 5× the icon radius

export function WindowDragCloseTarget() {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const windowIdRef = useRef<string | null>(null);
  const centerRef = useRef({ x: 0, y: 0 });
  const hoveringRef = useRef(false);
  const themeContext = useThemeOptional();
  const isDark = themeContext?.mode === "dark";

  useEffect(() => {
    const isInDropZone = (clientX: number, clientY: number) => {
      const { x: cx, y: cy } = centerRef.current;
      const dx = clientX - cx;
      const dy = clientY - cy;
      return Math.sqrt(dx * dx + dy * dy) <= DROP_ZONE_RADIUS;
    };

    const setHoverVisual = (hovering: boolean) => {
      if (hoveringRef.current === hovering) return;
      hoveringRef.current = hovering;
      const el = containerRef.current;
      const svg = svgRef.current;
      if (!el || !svg) return;

      el.style.transform = hovering ? "scale(2)" : "scale(1)";
      svg.style.stroke = hovering ? "#ef4444" : "";
    };

    const handleDragStart = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      windowIdRef.current = detail.windowId;
      hoveringRef.current = false;
      centerRef.current = {
        x: window.innerWidth / 2,
        y: window.innerHeight - 32 - TARGET_SIZE / 2,
      };

      const el = containerRef.current;
      if (el) {
        el.style.display = "flex";
        el.style.transform = "scale(1)";
      }
      const svg = svgRef.current;
      if (svg) svg.style.stroke = "";
    };

    const handleDragMove = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setHoverVisual(isInDropZone(detail.clientX, detail.clientY));
    };

    const handleDragEnd = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (isInDropZone(detail.clientX, detail.clientY) && windowIdRef.current) {
        window.dispatchEvent(
          new CustomEvent("window:drag-close", {
            detail: { windowId: windowIdRef.current },
          })
        );
      }
      const el = containerRef.current;
      if (el) {
        el.style.display = "none";
        el.style.transform = "scale(1)";
      }
      const svg = svgRef.current;
      if (svg) svg.style.stroke = "";
      hoveringRef.current = false;
      windowIdRef.current = null;
    };

    window.addEventListener("window:drag-start", handleDragStart);
    window.addEventListener("window:drag-move", handleDragMove);
    window.addEventListener("window:drag-end", handleDragEnd);

    return () => {
      window.removeEventListener("window:drag-start", handleDragStart);
      window.removeEventListener("window:drag-move", handleDragMove);
      window.removeEventListener("window:drag-end", handleDragEnd);
    };
  }, []);

  const iconSize = Math.round(TARGET_SIZE * 0.45);

  return (
    <div
      ref={containerRef}
      style={{
        display: "none",
        position: "fixed",
        bottom: 32,
        left: "50%",
        width: TARGET_SIZE,
        height: TARGET_SIZE,
        marginLeft: -TARGET_SIZE / 2,
        borderRadius: "50%",
        backgroundColor: isDark
          ? "rgba(30, 30, 30, 0.85)"
          : "rgba(255, 255, 255, 0.85)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        transform: "scale(1)",
        transition: "transform 0.25s ease-out, box-shadow 0.25s ease-out",
        boxShadow: isDark
          ? "0 4px 16px rgba(0, 0, 0, 0.5)"
          : "0 4px 16px rgba(0, 0, 0, 0.15)",
        border: isDark
          ? "1px solid rgba(255, 255, 255, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.12)",
        pointerEvents: "none",
      }}
    >
      <svg
        ref={svgRef}
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        stroke={isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          transition: "stroke 0.25s ease-out",
        }}
      >
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
      </svg>
    </div>
  );
}
